import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { DateInput } from "@/components/Interface/FormFields/DateInput/DateInput";
import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { Pill } from "@/components/Interface/Pill/Pill";
import { PHONE_REGEX } from "@/utils/constants";

import { NumberPortingFormProps } from "./NumberPorting";
import {
  formatNumber,
  mnpContractType,
  mnpTime,
  NUMBER_PREFIX,
} from "./NumberPorting.utils";
import { MsisdnValidationStatus } from "./useMsisdnValidation";

/** Group heading of a radio block inside the porting panel. */
const groupLabelClassName =
  "text-body-base font-semibold leading-none text-foreground-primary";

/** Explanatory copy inside the porting panel. */
const groupDescriptionClassName =
  "text-body-sm leading-none text-foreground-primary";

/**
 * Props for the NumberPortingFormFields component.
 */
type NumberPortingFormFieldsProps = {
  /** List of available providers. */
  providers: Record<string, number> | undefined;
  /** Whether the current selected provider is SWYPE Prepaid (requires special handling). */
  isSwypePrepaidProviderSelected: boolean;
  /** Current status of the MSISDN validation. */
  msisdnValidationStatus: MsisdnValidationStatus;
  /** Error message for the MSISDN field from backend or validation. */
  msisdnError: string;
  /** Whether a custom wish date can be selected by the customer. */
  wishdateSelectable: boolean;
  /** Function to trigger MSISDN validation. */
  validateMsisdn: (value: string) => void;
  /** Function to clear the MSISDN error. */
  clearMsisdnError: () => void;
};

/**
 * The porting panel: number, provider, contract type and termination timing of
 * the customer's current subscription.
 */
export const NumberPortingFormFields = ({
  providers,
  isSwypePrepaidProviderSelected,
  msisdnError,
  wishdateSelectable,
  validateMsisdn,
  clearMsisdnError,
}: NumberPortingFormFieldsProps) => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    control,
    resetField,
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<NumberPortingFormProps>();

  const currentNumber = watch("currentNumber");
  const mnpContractTypeValue = watch("mnpContractType");
  const mnpTimeValue = watch("mnpTime");

  // Logic to determine if certain fields should be disabled or shown.
  const terminationIsDisabled = !(
    mnpContractTypeValue?.toString() === mnpContractType.postpaid.id?.toString()
  );
  const terminationBeforeContractEnd =
    mnpTimeValue?.toString() === mnpTime.before.id.toString();

  return (
    <div
      data-testid="number-porting-panel"
      className="flex w-full flex-col gap-4 rounded-md bg-surface-quaternary p-4"
    >
      <div className="flex flex-col gap-1">
        <p className="text-body-sm font-semibold leading-none text-foreground-primary">
          {t("Onboarding.sections.number-porting.title")}
        </p>
        <p className={groupDescriptionClassName}>
          {t("Onboarding.sections.number-porting.subHeadline")}
        </p>
      </div>

      <div className="grid gap-6 tablet:grid-cols-2">
        <div className="relative">
          <FormField<NumberPortingFormProps>
            id="telephone-number-input"
            autocomplete="tel"
            name="currentNumber"
            label={t("Onboarding.sections.number-porting.currentNumber")}
            errors={errors}
            register={register}
            type="text"
            placeholder={t(
              "Onboarding.sections.number-porting.placeholder.currentNumber",
            )}
            onFocus={() => {
              // Pre-fill prefix if empty on focus.
              if (!currentNumber) {
                setValue("currentNumber", NUMBER_PREFIX);
              }
            }}
            options={{
              pattern: {
                value: PHONE_REGEX,
                message: t("Onboarding.sections.number-porting.numberFormat"),
              },
              required: t("translation:label.validation.required"),
              onChange: (e: any) => {
                const input = e.target as HTMLInputElement;
                const value = formatNumber(input.value, currentNumber);
                setValue("currentNumber", value);

                // Clear backend errors when the user starts typing again
                if (errors.currentNumber?.type === "backendError") {
                  clearErrors("currentNumber");
                }
                clearMsisdnError();
              },
              onBlur: (e: any) => {
                // Validate number when leaving the field
                const inputField = e.target as HTMLInputElement;
                validateMsisdn(inputField.value);
              },
            }}
          />

          {msisdnError && (
            <ErrorMessage message={msisdnError} name="currentNumber" />
          )}
        </div>

        {providers && (
          <Controller<NumberPortingFormProps>
            control={control}
            name="currentProvider"
            render={({ field }) => (
              <Combobox
                dataTestId="currentProviderCombobox"
                label={t("Onboarding.sections.number-porting.currentProvider")}
                field={field}
                options={Object.keys(providers)}
                placeholder={t(
                  "Onboarding.sections.number-porting.placeholder.currentProvider",
                )}
              />
            )}
          />
        )}

        {/* The visible labels name the radio groups (RadioGroup.Label) instead
            of a fieldset legend plus an aria-label, which named them twice. */}
        <RadioGroup
          data-testid="current-contract-type"
          className="flex flex-col gap-3"
          value={mnpContractTypeValue}
          onChange={(value: NumberPortingFormProps["mnpContractType"]) => {
            setValue("mnpContractType", value);
            if (value === `${mnpContractType.prepaid.id}`) {
              // Clear dependent fields when switching to prepaid.
              resetField("mnpTime");
              resetField("mnpWishDate");
            }
          }}
        >
          <RadioGroup.Label className={groupLabelClassName}>
            {t("Onboarding.sections.number-porting.currentContractType")}
          </RadioGroup.Label>

          <div className="flex flex-col gap-2">
            <RadioButtonOption<NumberPortingFormProps>
              name="mnpContractType"
              value={`${mnpContractType.prepaid.id}`}
              label={t("Onboarding.sections.number-porting.prepaid")}
            />
            <RadioButtonOption<NumberPortingFormProps>
              name="mnpContractType"
              value={`${mnpContractType.postpaid.id}`}
              label={t("Onboarding.sections.number-porting.postpaid")}
              // Postpaid is not allowed for providers like SWYPE.
              disabled={isSwypePrepaidProviderSelected}
            />
          </div>
        </RadioGroup>

        <div
          data-testid="termination"
          className={clsx(
            "flex flex-col gap-3",
            terminationIsDisabled && "opacity-50",
          )}
        >
          <RadioGroup
            className="flex flex-col gap-3"
            value={mnpTimeValue}
            onChange={(value: NumberPortingFormProps["mnpTime"]) => {
              setValue("mnpTime", value);
              if (value === `${mnpTime.after.id}`) {
                resetField("mnpWishDate");
              }
            }}
          >
            <span className="flex flex-col gap-1">
              <RadioGroup.Label className={groupLabelClassName}>
                {t("Onboarding.sections.number-porting.termination")}
              </RadioGroup.Label>
              <RadioGroup.Description className={groupDescriptionClassName}>
                {t("Onboarding.sections.number-porting.steps")}
              </RadioGroup.Description>
            </span>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <RadioButtonOption<NumberPortingFormProps>
                  name="mnpTime"
                  value={`${mnpTime.after.id}`}
                  label={t(
                    "Onboarding.sections.number-porting.afterContractEnds",
                  )}
                  disabled={terminationIsDisabled}
                />
                <Pill
                  className="bg-surface-quaternary text-foreground-info"
                  text={t("Onboarding.sections.number-porting.recommended")}
                  size="sm"
                />
              </div>

              <RadioButtonOption<NumberPortingFormProps>
                name="mnpTime"
                value={`${mnpTime.before.id}`}
                label={t(
                  "Onboarding.sections.number-porting.beforeContractEnds",
                )}
                disabled={terminationIsDisabled}
              />
            </div>
          </RadioGroup>

          {/* Warning for costs if premature termination is selected */}
          {terminationBeforeContractEnd && (
            <ErrorMessage
              message={t(
                "Onboarding.sections.number-porting.cost-warning",
              ).toString()}
              name={"mnpTime"}
            />
          )}

          {/* Wish date, only for premature termination if enabled */}
          {terminationBeforeContractEnd && wishdateSelectable && (
            <DateInput<NumberPortingFormProps>
              id="mnp-wish-date-input"
              name="mnpWishDate"
              label={t("Onboarding.sections.number-porting.chooseWishDate")}
              autocomplete="off"
              errors={errors}
              register={register}
              placeholder={t(
                "Onboarding.sections.number-porting.placeholder.wishDate",
              )}
              value={watch("mnpWishDate")}
              options={{
                required: t("translation:label.validation.required"),
                min: new Date().toISOString().split("T")[0],
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
